// Extrait de DataTab.js (handover bloc F1) — JSX, textes et appels API inchangés.
import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import { BarChart3, Download, Building2, Activity, Users, AlertTriangle, RefreshCw, CheckCircle } from "lucide-react";
import { KPICard } from "./shared";

export default function BusinessView({ filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.clinicId) params.append("clinic_id", filters.clinicId);
      if (filters.startDate) params.append("start_date", filters.startDate);
      if (filters.endDate) params.append("end_date", filters.endDate);
      
      const res = await api.get(`/data/business?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des métriques business");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.clinicId) params.append("clinic_id", filters.clinicId);
      if (filters.startDate) params.append("start_date", filters.startDate);
      if (filters.endDate) params.append("end_date", filters.endDate);
      
      const res = await api.get(`/data/export/business?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `business_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export business téléchargé");
    } catch (err) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  const kpis = data?.kpis || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-600" />
            Métriques Business (Agrégées)
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Indicateurs de volume et performance - pas de données patient individuelles
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || !data?.by_clinic?.length}
          data-testid="export-business-btn"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exporting ? "Export..." : "Export CSV"}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <KPICard
          icon={Building2}
          label="Cliniques actives"
          value={kpis.active_clinics || 0}
          color="emerald"
        />
        <KPICard
          icon={Users}
          label="Patients suivis"
          value={kpis.total_patients || 0}
          color="blue"
        />
        <KPICard
          icon={Activity}
          label="Check-ins"
          value={kpis.total_checkins || 0}
          color="purple"
        />
        <KPICard
          icon={AlertTriangle}
          label="Alertes"
          value={kpis.total_alerts || 0}
          subValue={`${kpis.alerts_critical || 0} critiques`}
          color="amber"
        />
        <KPICard
          icon={CheckCircle}
          label="Taux résolution"
          value={`${kpis.resolution_rate || 0}%`}
          color="teal"
        />
      </div>

      {/* Alert Distribution */}
      <div className="dash-card">
        <h4 className="font-medium text-slate-700 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          Répartition des alertes
        </h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-red-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{kpis.alerts_critical || 0}</p>
            <p className="text-sm text-slate-600">Critiques</p>
          </div>
          <div className="bg-orange-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-orange-600">{kpis.alerts_orange || 0}</p>
            <p className="text-sm text-slate-600">Orange</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{kpis.alerts_resolved || 0}</p>
            <p className="text-sm text-slate-600">Résolues</p>
          </div>
        </div>
      </div>

      {/* Clinic Table */}
      <div className="dash-card overflow-hidden p-0">
        <div className="p-4 border-b bg-slate-50">
          <h4 className="font-medium text-slate-700 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Activité par clinique
          </h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Clinique</th>
                <th className="text-right p-3 font-medium text-slate-600">Patients</th>
                <th className="text-right p-3 font-medium text-slate-600">Critiques</th>
                <th className="text-right p-3 font-medium text-slate-600">Check-ins</th>
                <th className="text-right p-3 font-medium text-slate-600">Alertes</th>
                <th className="text-right p-3 font-medium text-slate-600">Résolues</th>
                <th className="text-right p-3 font-medium text-slate-600">Taux</th>
                <th className="text-right p-3 font-medium text-slate-600">Interventions</th>
                <th className="text-right p-3 font-medium text-slate-600">Équipe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.by_clinic?.map((clinic, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="p-3 font-medium text-slate-800">{clinic.clinic_name}</td>
                  <td className="p-3 text-right text-slate-600">{clinic.patients_total}</td>
                  <td className="p-3 text-right">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      clinic.patients_critical > 0 ? "bg-red-100 text-red-700" : "text-slate-400"
                    }`}>
                      {clinic.patients_critical}
                    </span>
                  </td>
                  <td className="p-3 text-right text-slate-600">{clinic.checkins_total}</td>
                  <td className="p-3 text-right text-slate-600">{clinic.alerts_total}</td>
                  <td className="p-3 text-right text-green-600">{clinic.alerts_resolved}</td>
                  <td className="p-3 text-right">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      clinic.resolution_rate >= 80 ? "bg-green-100 text-green-700" :
                      clinic.resolution_rate >= 50 ? "bg-yellow-100 text-yellow-700" :
                      "bg-red-100 text-red-700"
                    }`}>
                      {clinic.resolution_rate}%
                    </span>
                  </td>
                  <td className="p-3 text-right text-slate-600">{clinic.interventions_total}</td>
                  <td className="p-3 text-right text-slate-500 text-xs">
                    {clinic.doctors}M / {clinic.nurses}IDE
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
